import React, { useEffect, useState } from 'react'
import { Image, Pressable, SafeAreaView, Text, useWindowDimensions, View } from 'react-native'
import { Menu, X } from 'lucide-react-native'
import { NAV_LINKS } from '../content'
import { useThemeValue } from '../ThemeContext'
import { Container, PrimaryButton, Section, ThemeSwitcher } from './ui'
import { baseFont, HEADER_HEIGHT, openExternal, RoutePath, RouteState } from '../utils/ui'

export function BrandLogo({ iconOnly = false }: { iconOnly?: boolean }) {
  const { theme } = useThemeValue()
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
      <View>
        <View
          style={{
            position: 'absolute',
            width: 40,
            height: 40,
            borderRadius: 14,
            backgroundColor: `${theme.colors.primary}30`,
          }}
        />
        <View
          style={{
            width: 40,
            height: 40,
            borderRadius: 14,
            overflow: 'hidden',
            borderWidth: 1,
            borderColor: theme.colors.border,
            backgroundColor: theme.colors.surface,
          }}
        >
          <Image
            source={require('../../examples/relayably/assets/logo-64.png')}
            style={{ width: '100%', height: '100%' }}
          />
        </View>
      </View>
      {!iconOnly ? (
        <Text
          style={{
            ...baseFont('700'),
            color: theme.colors.text,
            fontSize: 26,
            letterSpacing: -1.2,
          }}
        >
          Relay<Text style={{ color: theme.colors.primary }}>ably</Text>
        </Text>
      ) : null}
    </View>
  )
}

function NavLink({
  currentPath,
  onNavigate,
  item,
}: {
  currentPath: RoutePath
  onNavigate: (value: string) => void
  item: { name: string; path: RoutePath }
}) {
  const { theme } = useThemeValue()
  const active = currentPath === item.path

  return (
    <Pressable onPress={() => onNavigate(item.path)}>
      <View style={{ alignItems: 'center', gap: 6 }}>
        <Text
          style={{
            ...baseFont(active ? '700' : '500'),
            color: active ? theme.colors.text : theme.colors.inactive,
            fontSize: 14,
          }}
        >
          {item.name}
        </Text>
        <View
          style={{
            width: 24,
            height: 2,
            borderRadius: 999,
            backgroundColor: active ? theme.colors.primary : 'transparent',
          }}
        />
      </View>
    </Pressable>
  )
}

export function Navbar({
  route,
  onNavigate,
  onCtaClick,
}: {
  route: RouteState
  onNavigate: (value: string) => void
  onCtaClick: () => void
}) {
  const { width } = useWindowDimensions()
  const { theme } = useThemeValue()
  const [isOpen, setIsOpen] = useState(false)
  const isDesktop = width >= 920

  useEffect(() => {
    if (isDesktop) {
      setIsOpen(false)
    }
  }, [isDesktop])

  return (
    <View
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 40,
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.border,
        backgroundColor: theme.dark ? 'rgba(0,0,0,0.85)' : 'rgba(255,255,255,0.92)',
      }}
    >
      <SafeAreaView>
        <Container>
          <View
            style={{
              minHeight: HEADER_HEIGHT,
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 16,
            }}
          >
            <Pressable onPress={() => onNavigate('/')}>
              <BrandLogo />
            </Pressable>

            {isDesktop ? (
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 28 }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 28 }}>
                  {NAV_LINKS.map(link => (
                    <NavLink key={link.path} currentPath={route.path} item={link} onNavigate={onNavigate} />
                  ))}
                  <View
                    style={{
                      paddingHorizontal: 16,
                      paddingVertical: 8,
                      borderRadius: 999,
                      borderWidth: 1,
                      borderColor: theme.colors.border,
                      backgroundColor: theme.colors.surface,
                    }}
                  >
                    <Text
                      style={{
                        ...baseFont('700'),
                        fontSize: 10,
                        letterSpacing: 1,
                        color: theme.colors.inactive,
                        textTransform: 'uppercase',
                      }}
                    >
                      Beta Access Open
                    </Text>
                  </View>
                </View>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 16 }}>
                  <ThemeSwitcher />
                  <PrimaryButton rounded label="Request Access" onPress={onCtaClick} />
                </View>
              </View>
            ) : (
              <Pressable
                onPress={() => setIsOpen(value => !value)}
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 22,
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: theme.colors.surface,
                  borderWidth: 1,
                  borderColor: theme.colors.border,
                }}
              >
                {isOpen ? <X color={theme.colors.text} size={20} /> : <Menu color={theme.colors.text} size={20} />}
              </Pressable>
            )}
          </View>
        </Container>

        {!isDesktop && isOpen ? (
          <View
            style={{
              borderTopWidth: 1,
              borderTopColor: theme.colors.border,
              backgroundColor: theme.colors.background,
              paddingBottom: 20,
            }}
          >
            <Container>
              <View style={{ paddingTop: 16, gap: 18 }}>
                {NAV_LINKS.map(link => (
                  <Pressable
                    key={link.path}
                    onPress={() => {
                      setIsOpen(false)
                      onNavigate(link.path)
                    }}
                  >
                    <Text style={{ ...baseFont('700'), color: theme.colors.text, fontSize: 18 }}>{link.name}</Text>
                  </Pressable>
                ))}
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    paddingTop: 8,
                  }}
                >
                  <Text style={{ ...baseFont('500'), color: theme.colors.inactive, fontSize: 14 }}>Theme</Text>
                  <ThemeSwitcher compact />
                </View>
                <PrimaryButton
                  label="Request Access"
                  onPress={() => {
                    setIsOpen(false)
                    onCtaClick()
                  }}
                />
              </View>
            </Container>
          </View>
        ) : null}
      </SafeAreaView>
    </View>
  )
}

function FooterColumn({
  title,
  items,
  onNavigate,
}: {
  title: string
  items: { label: string; path: string }[]
  onNavigate: (value: string) => void
}) {
  const { theme } = useThemeValue()
  return (
    <View style={{ flex: 1, minWidth: 180, gap: 16 }}>
      <Text style={{ ...baseFont('700'), color: theme.colors.text, fontSize: 14, textTransform: 'uppercase', letterSpacing: 1 }}>{title}</Text>
      {items.map(item => (
        <Pressable key={item.label} onPress={() => onNavigate(item.path)}>
          <Text style={{ ...baseFont('500'), color: theme.colors.inactive, fontSize: 14 }}>{item.label}</Text>
        </Pressable>
      ))}
    </View>
  )
}

export function Footer({ onNavigate }: { onNavigate: (value: string) => void }) {
  const { theme } = useThemeValue()
  return (
    <Section background={theme.colors.surface} paddingTop={80} paddingBottom={80}>
      <Container>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 24, marginBottom: 52 }}>
          <View style={{ flex: 2, minWidth: 280, gap: 20 }}>
            <BrandLogo />
            <Text style={{ ...baseFont('500'), color: theme.colors.inactive, fontSize: 14, lineHeight: 24, maxWidth: 420 }}>
              We're building the high-tech relay layer for personal communications. Ensuring every call is
              handled with the reliability of a personal assistant.
            </Text>
          </View>
          <FooterColumn
            title="Product"
            items={[
              { label: 'Features', path: '/features' },
              { label: 'Pricing', path: '/pricing' },
              { label: 'Pillars', path: '/#pillars' },
            ]}
            onNavigate={onNavigate}
          />
          <FooterColumn
            title="Company"
            items={[
              { label: 'Vision', path: '/vision' },
              { label: 'Privacy', path: '/privacy' },
              { label: 'Terms', path: '/terms' },
            ]}
            onNavigate={onNavigate}
          />
        </View>

        <View
          style={{
            borderTopWidth: 1,
            borderTopColor: theme.colors.border,
            paddingTop: 28,
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 16,
          }}
        >
          <Text style={{ ...baseFont('700'), color: theme.colors.inactive, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase' }}>
            © 2026 Relayably Technologies. All rights reserved.
          </Text>
          <Pressable onPress={() => openExternal('https://x.com')}>
            <Text style={{ ...baseFont('700'), color: theme.colors.inactive, fontSize: 14 }}>𝕏</Text>
          </Pressable>
        </View>
      </Container>
    </Section>
  )
}
